import { MovementArticle, MovementHistoryLocationUnits, MovementTotals } from "types";
import { ENTRY_MOVEMENT_TYPES, EXIT_MOVEMENT_TYPES, MovementTypes } from "../constants";
import { dateISOFormat } from "helpers";

export const getInitialMovementDateValue = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return today;
};

export const getInitialMovementDate = () => {
  const today = getInitialMovementDateValue();

  return dateISOFormat(today.toISOString()) || "";
};

export const initMovementDate = (
  movementDate: string | null | undefined,
  setDate: (field: string, value: string) => void,
  options?: { setSettlement?: boolean }
) => {
  if (movementDate) return;

  const iso = getInitialMovementDate();

  setDate("date", iso);

  if (options?.setSettlement) {
    setDate("settlement_date", iso);
  }
};

export function getMovementTypeLabel(
  movementTypeId?: number,
  opts?: { isTransferFrom?: boolean; isTransferTo?: boolean }
): string {
  if (opts?.isTransferFrom) return "Transferencia (Desde)";
  if (opts?.isTransferTo) return "Transferencia (Hacia)";

  switch (movementTypeId) {
    case MovementTypes.Income:
      return "Ingreso";
    case MovementTypes.Discharge:
      return "Egreso";
    case MovementTypes.Additional:
      return "Adicional";
    case MovementTypes.Preparation:
      return "Pedido";
    case MovementTypes.Transfer:
      return "Transferencia";
    case MovementTypes.WorkOrder:
      return "Orden de trabajo";

    case MovementTypes.IncomeDraft:
      return "Ingreso (Borrador)";
    case MovementTypes.DischargeDraft:
      return "Egreso (Borrador)";
    case MovementTypes.PreparationDraft:
      return "Pedido (Borrador)";

    case MovementTypes.IncomeAdjustment:
      return "Ingreso (Ajuste)";
    case MovementTypes.DischargeAdjustment:
      return "Egreso (Ajuste)";

    default:
      return "-";
  }
}

export const formatMovementHistoryUnitCounts = (
  movementTypeId?: number,
  units?: MovementHistoryLocationUnits | null,
): string =>
  movementTypeId === MovementTypes.WorkOrder &&
  (units?.bundles != null || units?.packages != null)
    ? `${units?.bundles ?? 0} / ${units?.packages ?? 0}`
    : "–";

export const formatGroupedMovementHistoryUnitCounts = (
  movementTypeId?: number,
  locations?: MovementHistoryLocationUnits[] | null,
): string => {
  if (movementTypeId !== MovementTypes.WorkOrder) return "–";
  const available = locations?.filter(
    (location) => location.bundles != null || location.packages != null,
  );
  if (!available?.length) return "–";
  return formatMovementHistoryUnitCounts(movementTypeId, {
    bundles: available.reduce((total, location) => total + Number(location.bundles ?? 0), 0),
    packages: available.reduce((total, location) => total + Number(location.packages ?? 0), 0),
  });
};

export const isEntryMovement = (movementTypeId?: number) =>
  ENTRY_MOVEMENT_TYPES.includes(movementTypeId ?? -1);

export const isExitMovement = (movementTypeId?: number) =>
  EXIT_MOVEMENT_TYPES.includes(movementTypeId ?? -1);

type MovementTotalsSource = {
  articles?: Array<Pick<MovementArticle, "amount" | "subtotal">> | null;
};

export function calculateMovementTotals(
  movement: MovementTotalsSource,
): MovementTotals {
  const articles = movement.articles ?? [];

  const totalCantidad = articles.reduce(
    (acc, a) => acc + (Number(a.amount) || 0),
    0
  );

  const totalSubtotalUnidad = articles.reduce(
    (acc, a) => acc + (Number(a.subtotal) || 0),
    0
  );

  return {
    totalCantidad,
    totalSubtotalUnidad,
  };
}

export function calculateAvailableArticleLocationStock({
  movementArticles,
  articleLocationId,
  stock,
  reserved,
  notAvailable,
  inWorkOrders,
  excludeMovementArticleId,
}: {
  movementArticles: MovementArticle[];
  articleLocationId?: number | null;
  stock?: number | null;
  reserved?: number | null;
  notAvailable?: number | null;
  inWorkOrders?: number | null;
  excludeMovementArticleId?: number | string | null;
}): number {
  if (!articleLocationId) return 0;

  const getArticleLocationId = (article: MovementArticle) =>
    article.article_locations?.id ?? article.article_locations_id;

  const getReservedReferenceAmount = (article: MovementArticle) => {
    if (article.reserved_reference_amount != null) {
      return Number(article.reserved_reference_amount) || 0;
    }

    return article.is_reserved ? Number(article.amount) || 0 : 0;
  };

  const reservedUsedByOrderInThisMovement = movementArticles
    .filter((article) => {
      return getArticleLocationId(article) === articleLocationId && article.is_reserved;
    })
    .reduce((total, article) => total + getReservedReferenceAmount(article), 0);

  const stockUsedInThisMovement = movementArticles
    .filter((article) => {
      return (
        getArticleLocationId(article) === articleLocationId &&
        article.id !== excludeMovementArticleId
      );
    })
    .reduce((total, article) => total + (Number(article.amount) || 0), 0);

  const effectiveReserved = Math.max(
    0,
    Number(reserved ?? 0) - reservedUsedByOrderInThisMovement
  );

  return Math.max(
    0,
    Number(stock ?? 0) -
      effectiveReserved -
      Number(notAvailable ?? 0) -
      Number(inWorkOrders ?? 0) -
      stockUsedInThisMovement
  );
}
